import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

interface ScoreDetail {
  label: string
  score: number
  level: string
  color: string
}

interface ObligationResult {
  code: string
  name: string
  description?: string
  category: string
  priority: string
  legal_basis?: string
  status: string
  gap_description?: string
  recommendation?: string
}

interface DiagnosticResult {
  submission_id: string
  company_name: string
  sector?: string
  scores: Record<string, ScoreDetail>
  score_overall: number
  maturity_level: string
  obligations: ObligationResult[]
  total_obligations: number
  compliant_count: number
  partial_count: number
  non_compliant_count: number
  created_at: string
}

const MATURITY_LABELS: Record<string, { label: string; color: string; desc: string }> = {
  inicial: {
    label: 'Inicial',
    color: 'text-red-400 border-red-700 bg-red-900/20',
    desc: 'A organização está no início da jornada ESG. Há gaps significativos que precisam de atenção prioritária.',
  },
  intermediario: {
    label: 'Intermediário',
    color: 'text-yellow-400 border-yellow-700 bg-yellow-900/20',
    desc: 'Boas práticas existem em algumas dimensões, mas ainda há oportunidades relevantes de evolução.',
  },
  avancado: {
    label: 'Avançado',
    color: 'text-emerald-400 border-emerald-700 bg-emerald-900/20',
    desc: 'A organização demonstra maturidade ESG consistente com boas práticas de mercado.',
  },
}

const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  critical: { label: 'Crítico', color: 'bg-red-900/30 text-red-300 border-red-700' },
  high: { label: 'Alto', color: 'bg-orange-900/30 text-orange-300 border-orange-700' },
  medium: { label: 'Médio', color: 'bg-yellow-900/30 text-yellow-300 border-yellow-700' },
  low: { label: 'Baixo', color: 'bg-blue-900/30 text-blue-300 border-blue-700' },
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  compliant: { label: 'Conforme', color: 'bg-emerald-900/30 text-emerald-300 border-emerald-700' },
  partial: { label: 'Parcial', color: 'bg-yellow-900/30 text-yellow-300 border-yellow-700' },
  non_compliant: { label: 'Não Conforme', color: 'bg-red-900/30 text-red-300 border-red-700' },
  not_applicable: { label: 'N/A', color: 'bg-gray-800 text-gray-400 border-gray-700' },
}

function ScoreBar({ score, color }: { score: number; color: string }) {
  return (
    <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min(score, 100)}%`, backgroundColor: color }}
      />
    </div>
  )
}

function RadarChart({ scores }: { scores: Record<string, ScoreDetail> }) {
  const entries = Object.values(scores)
  if (entries.length < 3) return null

  const cx = 160
  const cy = 160
  const r = 120
  const n = entries.length
  const levels = [0.25, 0.5, 0.75, 1.0]

  const angleOf = (i: number) => (i * 2 * Math.PI) / n - Math.PI / 2
  const point = (val: number, i: number) => {
    const a = angleOf(i)
    const rv = (val / 100) * r
    return { x: cx + rv * Math.cos(a), y: cy + rv * Math.sin(a) }
  }

  // Polygon points for the data
  const dataPoints = entries.map((e, i) => point(e.score, i))
  const polyPoints = dataPoints.map(p => `${p.x},${p.y}`).join(' ')

  // Level rings
  const rings = levels.map(lv =>
    entries.map((_, i) => {
      const a = angleOf(i)
      const rv = lv * r
      return `${cx + rv * Math.cos(a)},${cy + rv * Math.sin(a)}`
    }).join(' ')
  )

  return (
    <svg viewBox="0 0 320 320" className="w-full max-w-xs mx-auto">
      {/* Level rings */}
      {rings.map((pts, i) => (
        <polygon key={i} points={pts} fill="none" stroke="#374151" strokeWidth="1" />
      ))}
      {/* Axis lines */}
      {entries.map((_, i) => {
        const a = angleOf(i)
        return (
          <line
            key={i}
            x1={cx} y1={cy}
            x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)}
            stroke="#374151" strokeWidth="1"
          />
        )
      })}
      {/* Data polygon */}
      <polygon
        points={polyPoints}
        fill="rgba(110, 231, 183, 0.2)"
        stroke="#6ee7b7"
        strokeWidth="2"
      />
      {/* Data points */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="#6ee7b7" />
      ))}
      {/* Labels */}
      {entries.map((e, i) => {
        const a = angleOf(i)
        const lx = cx + (r + 20) * Math.cos(a)
        const ly = cy + (r + 20) * Math.sin(a)
        return (
          <text
            key={i}
            x={lx} y={ly}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fill="#9ca3af"
          >
            {e.label.split(' ')[0]}
          </text>
        )
      })}
    </svg>
  )
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-950 dark:bg-gray-950 text-gray-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderWidth: '3px' }} />
        <p className="text-gray-400">Calculando seu diagnóstico ESG...</p>
      </div>
    </div>
  )
}

export default function DiagnosticResultPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, setResult] = useState<DiagnosticResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [activeOblTab, setActiveOblTab] = useState<string>('non_compliant')
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('nai_esg_theme') !== 'light' } catch { return true }
  })

  useEffect(() => {
    try { localStorage.setItem('nai_esg_theme', isDark ? 'dark' : 'light') } catch {}
  }, [isDark])

  // postMessage for iframe embed auto-resize
  useEffect(() => {
    const sendHeight = () => {
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'nai-diagnostic-resize', height: document.documentElement.scrollHeight }, '*')
      }
    }
    sendHeight()
    const timer = setTimeout(sendHeight, 300)
    window.addEventListener('resize', sendHeight)
    const observer = new MutationObserver(sendHeight)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true })
    return () => { clearTimeout(timer); window.removeEventListener('resize', sendHeight); observer.disconnect() }
  }, [])

  // Scroll parent to top on mount
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'nai-diagnostic-scroll-top' }, '*')
    }
  }, [])

  useEffect(() => {
    if (!id) return
    fetch(`/api/v1/diagnostics/${id}/result`)
      .then(r => {
        if (!r.ok) throw new Error(`Diagnóstico não encontrado (${r.status})`)
        return r.json()
      })
      .then(setResult)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [id])

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) return <LoadingSkeleton />

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">🔍</div>
          <h2 className="text-xl font-bold mb-2">Diagnóstico não encontrado</h2>
          <p className="text-gray-400 mb-6">{error || 'O link pode ter expirado ou estar incorreto.'}</p>
          <button
            onClick={() => navigate('/diagnostico-esg')}
            className="px-5 py-2.5 bg-emerald-500 text-gray-950 font-semibold rounded-lg hover:bg-emerald-400 transition-colors"
          >
            Fazer novo diagnóstico
          </button>
        </div>
      </div>
    )
  }

  const maturity = MATURITY_LABELS[result.maturity_level] || MATURITY_LABELS.inicial
  const scoreEntries = Object.values(result.scores)

  const oblByStatus = {
    non_compliant: result.obligations.filter(o => o.status === 'non_compliant'),
    partial: result.obligations.filter(o => o.status === 'partial'),
    compliant: result.obligations.filter(o => o.status === 'compliant'),
  }

  const activeObligations = oblByStatus[activeOblTab as keyof typeof oblByStatus] || []

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            {isDark ? (
              <img src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png" alt="NAI IT" style={{ height: "64px" }} className="mb-1" />
            ) : (
              <img src="https://nai-it.com.br/wp-content/uploads/2025/12/NAI-1.svg" alt="NAI IT" style={{ height: "64px" }} className="mb-1" />
            )}
            <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Diagnóstico ESG — Resultado</h1>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setIsDark(d => !d)}
              className={`p-2 rounded-lg text-sm transition-colors ${isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title={isDark ? 'Modo claro' : 'Modo escuro'}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            <button
              onClick={handleCopyLink}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${isDark ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
            >
              {copied ? '✅ Copiado!' : '🔗 Copiar link'}
            </button>
            <a
              href={`/api/v1/diagnostics/${id}/pdf`}
              className="px-3 py-2 text-xs font-semibold text-gray-950 bg-emerald-500 hover:bg-emerald-400 rounded-lg transition-colors flex items-center gap-1.5"
            >
              📄 PDF
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Company Header */}
        <div className={`rounded-xl p-6 border transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{result.company_name}</h2>
              {result.sector && <p className="text-gray-400 text-sm mt-1">{result.sector}</p>}
              <p className="text-gray-500 text-xs mt-1">
                Diagnóstico gerado em {new Date(result.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: 'long', year: 'numeric'
                })}
              </p>
            </div>
            <div className={`flex-shrink-0 px-4 py-2 border rounded-xl text-center ${maturity.color}`}>
              <div className="text-2xl font-bold">{result.score_overall.toFixed(0)}%</div>
              <div className="text-xs font-semibold uppercase tracking-wide">Maturidade {maturity.label}</div>
            </div>
          </div>
          <p className="mt-4 text-gray-300 text-sm">{maturity.desc}</p>
        </div>

        {/* Radar + Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Radar */}
          <div className={`rounded-xl p-5 border transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Radar ESG</h3>
            <RadarChart scores={result.scores} />
          </div>

          {/* Score Breakdown */}
          <div className={`rounded-xl p-5 border transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Scores por Dimensão</h3>
            <div className="space-y-4">
              {scoreEntries.map(score => (
                <div key={score.label}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-sm text-gray-200">{score.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{score.level}</span>
                      <span className="text-sm font-semibold" style={{ color: score.color }}>
                        {score.score.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <ScoreBar score={score.score} color={score.color} />
                </div>
              ))}
              {/* Overall */}
              <div className="pt-3 border-t border-gray-800">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-sm font-semibold text-white">Score Geral</span>
                  <span className="text-sm font-bold text-emerald-400">{result.score_overall.toFixed(0)}%</span>
                </div>
                <ScoreBar score={result.score_overall} color="#6ee7b7" />
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Summary */}
        {result.total_obligations > 0 && (
          <div className={`rounded-xl p-5 border transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">
              Obrigações Regulatórias — Resumo
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div className="text-center p-3 bg-emerald-900/20 border border-emerald-800/50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-400">{result.compliant_count}</div>
                <div className="text-xs text-gray-400 mt-1">Conforme</div>
              </div>
              <div className="text-center p-3 bg-yellow-900/20 border border-yellow-800/50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{result.partial_count}</div>
                <div className="text-xs text-gray-400 mt-1">Parcial</div>
              </div>
              <div className="text-center p-3 bg-red-900/20 border border-red-800/50 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{result.non_compliant_count}</div>
                <div className="text-xs text-gray-400 mt-1">Não Conforme</div>
              </div>
            </div>

            {/* Obligations Tabs */}
            <div className="flex gap-1 mb-4 border-b border-gray-800 pb-0">
              {(['non_compliant', 'partial', 'compliant'] as const).map(tab => {
                const count = oblByStatus[tab].length
                const labels: Record<string, string> = {
                  non_compliant: '🔴 Não Conforme',
                  partial: '🟡 Parcial',
                  compliant: '🟢 Conforme',
                }
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveOblTab(tab)}
                    className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors border-b-2 ${
                      activeOblTab === tab
                        ? 'text-white border-emerald-500 bg-gray-800'
                        : 'text-gray-400 border-transparent hover:text-gray-200'
                    }`}
                  >
                    {labels[tab]} ({count})
                  </button>
                )
              })}
            </div>

            {/* Obligations List */}
            {activeObligations.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Nenhuma obrigação nesta categoria.</p>
            ) : (
              <div className="space-y-3">
                {activeObligations.map(ob => {
                  const prio = PRIORITY_LABELS[ob.priority] || PRIORITY_LABELS.medium
                  const stat = STATUS_LABELS[ob.status] || STATUS_LABELS.not_applicable
                  return (
                    <div key={ob.code} className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">{ob.code}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${stat.color}`}>{stat.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${prio.color}`}>{prio.label}</span>
                        </div>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-100 mb-1">{ob.name}</h4>
                      {ob.legal_basis && (
                        <p className="text-xs text-gray-500 mb-2">📋 {ob.legal_basis}</p>
                      )}
                      {ob.gap_description && (
                        <p className="text-xs text-gray-400 mb-2">
                          <span className="text-gray-500">Gap: </span>{ob.gap_description}
                        </p>
                      )}
                      {ob.recommendation && (
                        <p className="text-xs text-emerald-400/80 mt-2 flex gap-1.5">
                          <span>💡</span><span>{ob.recommendation}</span>
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-r from-emerald-900/30 to-blue-900/30 border border-emerald-800/50 rounded-xl p-6 text-center">
          <h3 className="text-lg font-bold text-white mb-2">Próximos Passos</h3>
          <p className="text-gray-300 text-sm mb-5 max-w-xl mx-auto">
            Com base neste diagnóstico, a NAI IT pode ajudar sua empresa a implementar um
            programa ESG estruturado e em conformidade com as principais regulamentações.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://nai-it.com.br/contato"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 text-sm font-semibold text-gray-950 bg-emerald-500 hover:bg-emerald-400 rounded-lg transition-colors"
            >
              Falar com um Especialista
            </a>
            <a
              href={`/api/v1/diagnostics/${id}/pdf`}
              className="px-5 py-2.5 text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              📄 Baixar Relatório PDF
            </a>
            <button
              onClick={() => navigate('/diagnostico-esg')}
              className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
            >
              Refazer Diagnóstico
            </button>
          </div>
        </div>
      </div>

      <div className={`text-center py-8 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
        NAI IT — Plataforma de GRC e Sustentabilidade · Dados protegidos por LGPD
      </div>
    </div>
  )
}
